import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment-timezone';

import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { MatDialogSizes } from '../../../enums/mat-dialog-sizes.enum';

import {
    ActionPreviewCardPayload,
    ActionPreviewEdits,
    ActionPreviewSection,
    GableActivityItem,
    GableDismissReason,
    GablePendingProposal,
    GableService,
} from '../../../services/gable/gable.service';
import { GableDrawerService } from '../../../services/gable/gable-drawer.service';
import { GrowlerService } from '../../shared/growler/growler.service';
import {
    GableActionPreviewCardComponent,
} from '../../v2-layout/gable/action-preview/action-preview-card.component';
import {
    DismissReasonDialogComponent,
    DismissReasonDialogResult,
} from '../../v2-layout/gable/dismiss-reason-dialog/dismiss-reason-dialog.component';

export type GableActivityFilter =
    | 'all'
    | 'requests'
    | 'violations'
    | 'payments'
    | 'communications'
    | 'amenities'
    | 'calendar'
    | 'surveys'
    | 'voting';

/**
 * Module filter → action-type sets. The backend filters by actionTypes;
 * the module grouping is a pure UI concept, so it lives here. Exported —
 * the drawer host uses the same map for the hero's module quick links.
 */
export const MODULE_ACTION_TYPES: Record<Exclude<GableActivityFilter, 'all'>, string[]> = {
    requests: ['set_request_status', 'reply_to_request'],
    violations: ['send_violation_notice', 'update_violation_status'],
    payments: ['categorize_transaction', 'send_payment_reminder', 'approve_payable', 'create_charge', 'issue_account_credit'],
    communications: ['email_unit_owners', 'email_board_summary', 'send_email'],
    amenities: ['approve_amenity_booking', 'cancel_amenity_booking'],
    calendar: ['create_calendar_event'],
    surveys: ['send_survey', 'close_survey'],
    voting: ['close_ballot', 'send_ballot_reminder', 'share_ballot_results'],
};

/** Dismiss-reason enum → the human-readable line shown in the Activity feed. */
export const DISMISS_REASON_LABELS: Record<GableDismissReason, string> = {
    not_needed: 'Not needed right now',
    manual: 'I\'ll handle this manually',
    wrong: 'Gable got this wrong',
    policy: 'Against our policy',
};

export const MODULE_LABELS: Record<Exclude<GableActivityFilter, 'all'>, string> = {
    requests: 'Requests',
    violations: 'Violations',
    payments: 'Payments & Transactions',
    communications: 'Communications',
    amenities: 'Amenities',
    calendar: 'Calendar',
    surveys: 'Surveys',
    voting: 'Voting',
};

/** Module for an action type ('other' for legacy CRUD / unknown types). */
export function moduleForActionType(actionType: string | null): Exclude<GableActivityFilter, 'all'> | 'other' {
    if (actionType !== null) {
        for (const [module, types] of Object.entries(MODULE_ACTION_TYPES)) {
            if (types.includes(actionType)) {
                return module as Exclude<GableActivityFilter, 'all'>;
            }
        }
    }
    return 'other';
}

/** One pending proposal in the review queue + the card payload it renders as. */
interface ReviewEntry {
    proposal: GablePendingProposal;
    /** Non-null for typed-action rows (preview persisted server-side). */
    cardPayload: ActionPreviewCardPayload | null;
    /** Legacy CRUD rows only — in-flight approve/reject spinner state. */
    busy: boolean;
    /** Legacy CRUD rows only — set once decided so the row fades in place. */
    resolvedLabel: string | null;
}

/** Pending review entries grouped by module for the group header + Approve All. */
interface ReviewGroup {
    module: Exclude<GableActivityFilter, 'all'> | 'other';
    label: string;
    entries: ReviewEntry[];
}

interface FeedDay {
    label: string;
    items: GableActivityItem[];
}

/** Dismissible "N approved" confirmation shown at the top of the queue. */
interface SuccessBanner {
    message: string;
    /** Module filter the "View →" link deep-links the feed to. */
    module: GableActivityFilter;
}

/**
 * Gable drawer — ACTIVITY view panel (SD-7969, real data).
 *
 * Review queue: the user's pending proposals (typed-action rows render the
 * same app-gable-action-preview-card as chat — confirm/refine/cancel work
 * here identically; legacy CRUD rows get summary + approve/reject buttons).
 * Feed: decided-proposal history from GET proposals/activity, day-grouped,
 * filterable by module chips (client maps module → action-type set).
 */
@UntilDestroy()
@Component({
    selector: 'app-gable-activity-panel',
    templateUrl: './gable-activity-panel.component.html',
    styleUrls: ['./gable-activity-panel.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, GableActionPreviewCardComponent],
})
export class GableActivityPanelComponent implements OnInit, OnDestroy {
    /** Preselected module chip — future chat summary boxes deep-link with this. */
    @Input() initialFilter: GableActivityFilter = 'all';

    reviewEntries: ReviewEntry[] = [];
    queueLoaded = false;

    /** Which typed queue card is expanded (accordion — single-open at a time). */
    expandedEntryId: number | null = null;

    /** Module key currently mid Approve-All (drives the header button spinner). */
    approvingGroup: string | null = null;

    /** Top-of-queue success confirmation; auto-clears ~8s after it appears. */
    successBanner: SuccessBanner | null = null;
    private _bannerTimer: ReturnType<typeof setTimeout> | null = null;

    feedItems: GableActivityItem[] = [];
    feedLoading = false;
    feedLoaded = false;
    /** Last feed fetch failed — template shows the error state + Retry. */
    feedError = false;
    private _feedPage = 1;
    private _feedLastPage = 1;

    activeFilter: GableActivityFilter = 'all';

    readonly filters: { key: GableActivityFilter; label: string }[] = [
        { key: 'all', label: 'All activity' },
        { key: 'requests', label: 'Requests' },
        { key: 'violations', label: 'Violations' },
        { key: 'payments', label: 'Payments & Transactions' },
        { key: 'communications', label: 'Communications' },
        { key: 'amenities', label: 'Amenities' },
        { key: 'calendar', label: 'Calendar' },
        { key: 'surveys', label: 'Surveys' },
        { key: 'voting', label: 'Voting' },
    ];

    constructor(
            private _drawer: GableDrawerService,
            private _service: GableService,
            private _cdr: ChangeDetectorRef,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
    ) {
    }

    ngOnDestroy(): void {
        this._clearBannerTimer();
    }

    ngOnInit(): void {
        this.activeFilter = this.initialFilter;

        this._drawer.pendingProposals$
                .pipe(untilDestroyed(this))
                .subscribe((proposals) => {
                    this._mergeReviewEntries(proposals);
                    this.queueLoaded = true;
                    this._cdr.markForCheck();
                });
        this._drawer.loadPendingProposals();
        this._loadFeed(true);
    }

    get waitingCount(): number {
        return this.reviewEntries
                .filter((e) => (e.cardPayload ? e.cardPayload.state === 'pending' : e.resolvedLabel === null))
                .length;
    }

    get queueEmpty(): boolean {
        return this.queueLoaded && this.reviewEntries.length === 0;
    }

    /**
     * Pending review entries grouped by module, preserving first-seen order.
     * Drives the per-module group header + its Approve-All button.
     */
    get reviewGroups(): ReviewGroup[] {
        // Map preserves first-seen insertion order, so no separate order array.
        const groups = new Map<string, ReviewGroup>();
        for (const entry of this.reviewEntries) {
            const module = moduleForActionType(entry.proposal.actionType);
            // Honor the active filter chip — 'all' shows every module.
            if (this.activeFilter !== 'all' && module !== this.activeFilter) {
                continue;
            }
            const existing = groups.get(module);
            if (existing === undefined) {
                groups.set(module, {
                    module,
                    label: module === 'other' ? 'Other' : MODULE_LABELS[module],
                    entries: [entry],
                });
            } else {
                existing.entries = [...existing.entries, entry];
            }
        }
        return Array.from(groups.values());
    }

    /** Approve-All is offered only when a group has ≥1 pending TYPED card entry. */
    groupHasPendingTyped(group: ReviewGroup): boolean {
        return group.entries.some((e) => e.cardPayload !== null && e.cardPayload.state === 'pending');
    }

    trackByGroupModule(_index: number, group: ReviewGroup): string {
        return group.module;
    }

    /** Module identity icon for a review-group header (B3). */
    groupIcon(group: ReviewGroup): string {
        switch (group.module) {
            case 'requests':
                return 'ph-seal-question';
            case 'violations':
                return 'ph-seal-warning';
            case 'payments':
                return 'ph-money-wavy';
            case 'communications':
                return 'ph-envelope-simple';
            default:
                return 'ph-sparkle';
        }
    }

    // ---------------------------------------------------------------------
    // Collapsed / expanded accordion (typed queue cards only)
    // ---------------------------------------------------------------------

    isExpanded(entry: ReviewEntry): boolean {
        return this.expandedEntryId === entry.proposal.id;
    }

    toggleExpanded(entry: ReviewEntry): void {
        this.expandedEntryId = this.isExpanded(entry) ? null : entry.proposal.id;
        this._cdr.markForCheck();
    }

    /** Collapsed-row title — the card's header section title, else the summary. */
    entryTitle(entry: ReviewEntry): string {
        const header = this._section(entry, 'header');
        if (header && header.kind === 'header' && header.title) {
            return header.title;
        }
        return entry.proposal.summary;
    }

    /** Collapsed-row one-line summary — header subtitle, else the proposal summary. */
    entrySummary(entry: ReviewEntry): string {
        const header = this._section(entry, 'header');
        if (header && header.kind === 'header' && header.subtitle) {
            return header.subtitle;
        }
        return entry.proposal.summary;
    }

    /** Collapsed-row amount — the outcome section's formatted USD, when present. */
    entryAmount(entry: ReviewEntry): string | null {
        const outcome = this._section(entry, 'outcome');
        return outcome && outcome.kind === 'outcome' ? (outcome.amount ?? null) : null;
    }

    /** True once a typed entry has left the pending state (committed / cancelled). */
    entryResolved(entry: ReviewEntry): boolean {
        return entry.cardPayload !== null && entry.cardPayload.state !== 'pending';
    }

    private _section(entry: ReviewEntry, kind: ActionPreviewSection['kind']): ActionPreviewSection | null {
        const sections = entry.cardPayload?.preview?.sections ?? [];
        return sections.find((s) => s.kind === kind) ?? null;
    }

    get canLoadMore(): boolean {
        return this._feedPage < this._feedLastPage;
    }

    /**
     * Feed items grouped by MODULE (per Catherine 2026-07-11 — the hero's
     * module quick links land here, so the tab mirrors that mental model).
     * Groups are ordered by their newest item (API is newest-first); items
     * inside a group keep that order too.
     */
    get feedDays(): FeedDay[] {
        const groups = new Map<string, GableActivityItem[]>();
        for (const item of this.feedItems) {
            const label = this._moduleLabel(item.actionType);
            groups.set(label, [...(groups.get(label) ?? []), item]);
        }
        return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
    }

    get feedEmptyLabel(): string {
        return this.activeFilter === 'all'
            ? 'No decided actions yet'
            : `No ${this.filters.find((f) => f.key === this.activeFilter)?.label.toLowerCase()} activity yet`;
    }

    setFilter(filter: GableActivityFilter): void {
        if (this.activeFilter === filter) {
            return;
        }
        this.activeFilter = filter;
        this._loadFeed(true);
    }

    loadMore(): void {
        if (this.feedLoading || !this.canLoadMore) {
            return;
        }
        this._feedPage += 1;
        this._loadFeed(false);
    }

    /** Retry after a failed fetch — fresh load when the list is empty, else re-fetch the failed page. */
    retryFeed(): void {
        if (this.feedLoading) {
            return;
        }
        if (this.feedItems.length === 0) {
            this._loadFeed(true);
            return;
        }
        // The error handler rolled _feedPage back to the last good page —
        // re-advance to the page that failed (mirrors loadMore) so retry
        // fetches the MISSING page instead of duplicating the last one.
        this._feedPage += 1;
        this._loadFeed(false);
    }

    // ---------------------------------------------------------------------
    // Review queue — typed-action card handlers (mirror the drawer-host)
    // ---------------------------------------------------------------------

    onCardConfirm(entry: ReviewEntry, event: { proposalId: number; edits: ActionPreviewEdits }): void {
        // While an Approve All run is in flight the untouched cards stay
        // interactive — a manual confirm here would double-commit the same
        // proposal under a second idempotency key.
        if (this.approvingGroup !== null || entry.busy) {
            return;
        }
        const idempotencyKey = this._randomUuid();
        this._patchEntryState(entry, 'committing');
        this._drawer.commitActionProposal(event.proposalId, idempotencyKey, event.edits)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this._patchEntryState(entry, 'committed', res.result as Record<string, unknown>);
                        // Keep any chat copy of this card in sync (no-op without one).
                        this._drawer.patchActionPreviewState(event.proposalId, 'committed', res.result as Record<string, unknown>);
                        this._drawer.removeProposal(event.proposalId);
                        this._showBanner(`${entry.proposal.summary} — done`, this._moduleFilterFor(entry));
                        this._loadFeed(true);
                    },
                    error: () => {
                        this._patchEntryState(entry, 'pending');
                    },
                });
    }

    /**
     * Typed-card dismiss ("Don't change this"): open the reason dialog first.
     * A null result (Cancel / backdrop) aborts — the proposal stays pending.
     */
    onCardCancel(entry: ReviewEntry, event: { proposalId: number }): void {
        if (this.approvingGroup !== null || entry.busy) {
            return;
        }
        this._openDismissDialog((reason) => this._rejectTypedEntry(entry, event.proposalId, reason));
    }

    onCardSnooze(entry: ReviewEntry, event: { proposalId: number }): void {
        if (this.approvingGroup !== null || entry.busy) {
            return;
        }
        entry.busy = true;
        this._drawer.snoozeProposal(event.proposalId)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => {
                        // Snoozed items simply drop out of the pending queue (they
                        // return lazily once snoozed_until lapses, server-side).
                        this._drawer.removeProposal(event.proposalId);
                        this._growler.success('Snoozed', 'Snoozed until tomorrow morning.');
                        this._loadFeed(true);
                    },
                    error: () => {
                        entry.busy = false;
                        // Re-issue the pending payload so the card unlocks the
                        // action row it froze when the snooze was clicked.
                        this._patchEntryState(entry, 'pending');
                        this._growler.oops('Couldn\'t snooze that suggestion. Please try again.');
                    },
                });
    }

    private _rejectTypedEntry(entry: ReviewEntry, proposalId: number, reason: GableDismissReason): void {
        this._drawer.rejectProposal(proposalId, reason)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => {
                        this._patchEntryState(entry, 'cancelled');
                        this._drawer.patchActionPreviewState(proposalId, 'cancelled');
                        this._drawer.removeProposal(proposalId);
                        this._growler.success('Dismissed', 'Gable noted your reason.');
                        this._loadFeed(true);
                    },
                    error: () => {
                        // Treat as cancelled locally either way — matches the chat flow.
                        this._patchEntryState(entry, 'cancelled');
                        this._drawer.removeProposal(proposalId);
                    },
                });
    }

    onCardUtility(entry: ReviewEntry, event: { proposalId: number; utilityActionId: string; edits: ActionPreviewEdits }): void {
        this._drawer.refineActionProposal(event.proposalId, event.utilityActionId, event.edits)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        if (entry.cardPayload !== null) {
                            entry.cardPayload = {
                                ...entry.cardPayload,
                                preview: res.preview as ActionPreviewCardPayload['preview'],
                            };
                        }
                        this._drawer.patchActionPreview(event.proposalId, { preview: res.preview, edits: res.edits });
                        this._cdr.markForCheck();
                    },
                });
    }

    // ---------------------------------------------------------------------
    // Review queue — legacy CRUD rows
    // ---------------------------------------------------------------------

    approveCrud(entry: ReviewEntry): void {
        if (entry.busy || entry.resolvedLabel !== null) {
            return;
        }
        entry.busy = true;
        this._drawer.approveProposal(entry.proposal.id, this._randomUuid())
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: () => this._resolveCrud(entry, 'Approved'),
                    error: () => {
                        entry.busy = false;
                        this._cdr.markForCheck();
                    },
                });
    }

    rejectCrud(entry: ReviewEntry): void {
        if (entry.busy || entry.resolvedLabel !== null) {
            return;
        }
        this._openDismissDialog((reason) => {
            entry.busy = true;
            this._cdr.markForCheck();
            this._drawer.rejectProposal(entry.proposal.id, reason)
                    .pipe(untilDestroyed(this))
                    .subscribe({
                        next: () => this._resolveCrud(entry, 'Rejected'),
                        error: () => {
                            entry.busy = false;
                            this._cdr.markForCheck();
                        },
                    });
        });
    }

    // ---------------------------------------------------------------------
    // Approve-all (per module group)
    // ---------------------------------------------------------------------

    /**
     * Commit every pending typed card in a module group, one at a time (each
     * with a fresh idempotency key). Stops on the first error, leaving the
     * remaining items pending; shows a success banner for whatever committed.
     *
     * @deprecated v1: "Approve all" was removed from the UI (its button is
     * commented out in the template) — every suggestion is reviewed
     * individually. This handler + `_approveSequential`/`_finishApproveAll` +
     * the `approvingGroup` state are retained so the feature can be re-enabled
     * without a rebuild; do not wire new callers to it.
     */
    approveAllInGroup(group: ReviewGroup): void {
        if (this.approvingGroup !== null) {
            return;
        }
        const pending = group.entries.filter(
            (e) => e.cardPayload !== null
                && e.cardPayload.state === 'pending'
                // A card with a `choice` section exists to force the human's
                // pick — bulk approval must never submit Gable's best guess,
                // so those stay in the queue for one-by-one review.
                && !this._hasChoiceSection(e),
        );
        if (pending.length === 0) {
            return;
        }
        this.approvingGroup = group.module;
        this._cdr.markForCheck();
        this._approveSequential(group, pending, 0, 0);
    }

    private _approveSequential(
        group: ReviewGroup,
        entries: ReviewEntry[],
        index: number,
        successCount: number,
    ): void {
        if (index >= entries.length) {
            this._finishApproveAll(group, successCount);
            return;
        }
        const entry = entries[index];
        const idempotencyKey = this._randomUuid();
        this._patchEntryState(entry, 'committing');
        // Approve-All commits with the server-baked params (no per-card edits);
        // a card that still needs input — e.g. an unresolved choice — 422s and
        // stops the run, leaving it (and the rest) pending for manual review.
        this._drawer.commitActionProposal(entry.proposal.id, idempotencyKey, {})
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this._patchEntryState(entry, 'committed', res.result as Record<string, unknown>);
                        this._drawer.patchActionPreviewState(entry.proposal.id, 'committed', res.result as Record<string, unknown>);
                        this._drawer.removeProposal(entry.proposal.id);
                        this._approveSequential(group, entries, index + 1, successCount + 1);
                    },
                    error: () => {
                        this._patchEntryState(entry, 'pending');
                        this._finishApproveAll(group, successCount);
                    },
                });
    }

    private _finishApproveAll(group: ReviewGroup, successCount: number): void {
        this.approvingGroup = null;
        if (successCount > 0) {
            this._showBanner(
                `${successCount} approved in ${group.label}`,
                group.module === 'other' ? 'all' : group.module,
            );
            this._loadFeed(true);
        }
        this._cdr.markForCheck();
    }

    // ---------------------------------------------------------------------
    // Success banner
    // ---------------------------------------------------------------------

    private _showBanner(message: string, module: GableActivityFilter): void {
        this.successBanner = { message, module };
        this._clearBannerTimer();
        this._bannerTimer = setTimeout(() => {
            this.successBanner = null;
            this._bannerTimer = null;
            this._cdr.markForCheck();
        }, 8000);
        this._cdr.markForCheck();
    }

    dismissBanner(): void {
        this.successBanner = null;
        this._clearBannerTimer();
    }

    /** "View →" — deep-link the feed to the banner's module filter. */
    onBannerView(): void {
        if (this.successBanner !== null) {
            this.setFilter(this.successBanner.module);
            this.dismissBanner();
        }
    }

    private _clearBannerTimer(): void {
        if (this._bannerTimer !== null) {
            clearTimeout(this._bannerTimer);
            this._bannerTimer = null;
        }
    }

    private _moduleFilterFor(entry: ReviewEntry): GableActivityFilter {
        const module = moduleForActionType(entry.proposal.actionType);
        return module === 'other' ? 'all' : module;
    }

    /** True when the card carries a low-confidence `choice` section. */
    private _hasChoiceSection(entry: ReviewEntry): boolean {
        const sections = entry.cardPayload?.preview?.sections ?? [];
        return sections.some((s) => (s as { kind?: string }).kind === 'choice');
    }

    /**
     * Open the dismiss-reason dialog and run `onReason` only when the user
     * picks one. A null result (Cancel / backdrop) aborts — nothing rejects.
     */
    private _openDismissDialog(onReason: (reason: GableDismissReason) => void): void {
        this._dialog
                .open<DismissReasonDialogComponent, undefined, DismissReasonDialogResult>(
                    DismissReasonDialogComponent,
                    // gable-v2-dialog, NOT v2-dialog: the drawer lives outside
                    // the v2-layout host, so the scoped .v2-dialog reset never
                    // reaches the CDK overlay. The global gable-v2-dialog class
                    // (styles/ui/app.scss) applies resetMatDialog for us.
                    { panelClass: ['gable-v2-dialog'], width: MatDialogSizes.XS, autoFocus: false },
                )
                .afterClosed()
                .pipe(untilDestroyed(this))
                .subscribe((reason) => {
                    if (reason) {
                        onReason(reason);
                    }
                });
    }

    // ---------------------------------------------------------------------
    // Feed presentation helpers
    // ---------------------------------------------------------------------

    statusLabel(item: GableActivityItem): string {
        switch (item.status) {
            case 'approved':
                return 'You approved';
            case 'rejected':
                return 'You rejected';
            case 'expired':
                return 'Expired unactioned';
            case 'snoozed':
                return 'Snoozed';
        }
    }

    statusPillClass(item: GableActivityItem): string {
        switch (item.status) {
            case 'approved':
                return 'pill-green';
            case 'rejected':
                return 'pill-gray';
            case 'expired':
                return 'pill-gray';
            case 'snoozed':
                return 'pill-orange';
        }
    }

    /** Human-readable dismiss reason for a rejected feed row, or null if none. */
    dismissReasonLabel(item: GableActivityItem): string | null {
        const reason = item.dismissReason;
        if (!reason) {
            return null;
        }
        return DISMISS_REASON_LABELS[reason as GableDismissReason] ?? null;
    }

    domainToneClass(actionType: string | null): string {
        switch (moduleForActionType(actionType)) {
            case 'violations':
                return 'tone-orange';
            case 'payments':
                return 'tone-green';
            case 'surveys':
            case 'voting':
                return 'tone-purple';
            default:
                return 'tone-cyan';
        }
    }

    domainIcon(actionType: string | null): string {
        if (actionType === null) {
            return 'ph-pencil-simple';
        }
        switch (moduleForActionType(actionType)) {
            case 'requests':
                return 'ph-wrench';
            case 'violations':
                return 'ph-warning';
            case 'payments':
                return 'ph-currency-dollar';
            case 'amenities':
                return 'ph-swimming-pool';
            case 'calendar':
                return 'ph-calendar-check';
            case 'surveys':
                return 'ph-clipboard-text';
            case 'voting':
                return 'ph-checks';
            default:
                return 'ph-envelope-simple';
        }
    }

    /** "2:11 PM" today, "Yesterday 12:16 PM", else "July 9 11:37 AM" — org timezone, groups are per-module so each row carries its day. */
    decidedTime(item: GableActivityItem): string {
        const iso = item.decidedAt ?? item.createdAt;
        if (iso === null) {
            return '';
        }
        const m = moment.tz(iso, this._orgTz());
        const time = m.format('h:mm A');
        const day = this._dayLabel(m);
        return day === 'Today' ? time : `${day} ${time}`;
    }

    trackByProposalId(index: number, entry: ReviewEntry): number {
        return entry.proposal.id;
    }

    trackByItemId(index: number, item: GableActivityItem): number {
        return item.id;
    }

    trackByDayLabel(index: number, day: FeedDay): string {
        return day.label;
    }

    trackByFilterKey(index: number, filter: { key: string }): string {
        return filter.key;
    }

    // ---------------------------------------------------------------------

    /**
     * Merge the latest pending list into local entries, preserving entries
     * that just resolved locally (committed/cancelled cards stay visible as
     * an audit trail until the next tab visit rebuilds the list).
     */
    private _mergeReviewEntries(proposals: GablePendingProposal[]): void {
        const existing = new Map(this.reviewEntries.map((e) => [e.proposal.id, e]));
        const next: ReviewEntry[] = proposals.map((proposal) => {
            const prior = existing.get(proposal.id);
            if (prior !== undefined) {
                return prior;
            }
            return {
                proposal,
                cardPayload: proposal.actionType !== null && proposal.preview !== null
                    ? {
                        proposal_id: proposal.id,
                        action_type: proposal.actionType,
                        preview: proposal.preview as ActionPreviewCardPayload['preview'],
                        state: 'pending',
                    }
                    : null,
                busy: false,
                resolvedLabel: null,
            };
        });
        // Keep locally-resolved entries not in the fresh pending list.
        const resolvedLeftovers = this.reviewEntries.filter((entry) => {
            const resolved = entry.cardPayload !== null
                ? entry.cardPayload.state === 'committed' || entry.cardPayload.state === 'cancelled'
                : entry.resolvedLabel !== null;
            return resolved && !proposals.some((p) => p.id === entry.proposal.id);
        });
        this.reviewEntries = [...next, ...resolvedLeftovers];
    }

    private _patchEntryState(
        entry: ReviewEntry,
        state: ActionPreviewCardPayload['state'],
        result: Record<string, unknown> | null = null,
    ): void {
        if (entry.cardPayload === null) {
            return;
        }
        const next: ActionPreviewCardPayload & { result?: Record<string, unknown> } = {
            ...entry.cardPayload,
            state,
        };
        if (result !== null) {
            next.result = result;
        }
        entry.cardPayload = next;
        this._cdr.markForCheck();
    }

    private _resolveCrud(entry: ReviewEntry, label: string): void {
        entry.busy = false;
        entry.resolvedLabel = label;
        this._drawer.removeProposal(entry.proposal.id);
        this._loadFeed(true);
        this._cdr.markForCheck();
    }

    private _loadFeed(reset: boolean): void {
        if (reset) {
            this._feedPage = 1;
        }
        this.feedLoading = true;
        this.feedError = false;
        const actionTypes = this.activeFilter === 'all' ? undefined : MODULE_ACTION_TYPES[this.activeFilter];
        this._service.getProposalActivity(actionTypes, this._feedPage)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (res) => {
                        this.feedItems = reset ? res.items : [...this.feedItems, ...res.items];
                        this._feedLastPage = res.lastPage;
                        this.feedLoading = false;
                        this.feedLoaded = true;
                        this._cdr.markForCheck();
                    },
                    error: (err) => {
                        // Roll the page back so a retrying load-more doesn't
                        // skip the page that never arrived.
                        if (!reset) {
                            this._feedPage = Math.max(1, this._feedPage - 1);
                        }
                        console.error('[GableActivityPanel] feed load failed', err);
                        this.feedLoading = false;
                        this.feedLoaded = true;
                        this.feedError = true;
                        this._cdr.markForCheck();
                    },
                });
    }

    private _moduleLabel(actionType: string | null): string {
        const module = moduleForActionType(actionType);
        return module === 'other' ? 'Other' : MODULE_LABELS[module];
    }

    /** Org timezone from the JWT, per angular/timezone-and-dates.md. */
    private _orgTz(): string {
        return LegFiJwtService.getTimezone() || 'UTC';
    }

    private _dayLabel(m: moment.Moment): string {
        const today = moment.tz(this._orgTz());
        if (m.isSame(today, 'day')) {
            return 'Today';
        }
        if (m.isSame(today.clone().subtract(1, 'day'), 'day')) {
            return 'Yesterday';
        }
        return m.format('MMMM D');
    }

    private _randomUuid(): string {
        const c = window.crypto;
        if (c?.randomUUID !== undefined) {
            return c.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    }
}
